import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const GROUP_LABELS = { driver: 'Drivers', sales: 'Sales', dispatcher: 'Dispatchers', admin: 'Admin', maintenance: 'Maintenance' };
const medalColors = ['#fbbf24', '#94a3b8', '#c2845c']; // gold, silver, bronze

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */
function fmt(value, mt, sign) {
  if (value === null || value === undefined) return '\u2014';
  let formatted;
  switch (mt) {
    case 'count': formatted = Math.round(value).toLocaleString(); break;
    case 'percentage': formatted = `${value.toFixed(1)}%`; break;
    case 'currency': formatted = `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; break;
    case 'duration': formatted = `${Math.round(value)}m`; break;
    case 'score': formatted = value.toFixed(1); break;
    default: formatted = String(value);
  }
  if (sign === 'negative' && value > 0) return `−${formatted}`;
  return formatted;
}

function ini(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

/* ═══════════════════════════════════════════════════════════════
   PREPROCESS KPIS — annotate with displayValue
   The backend's `total` field already honors aggregation_mode:
   - 'sum' mode: total = sum across rounds
   - 'latest' mode: total = value from the most recent round with data

   So displayValue == total in all cases. We just attach a latestRound
   reference for use in UI labels and subtitles.
   ═══════════════════════════════════════════════════════════════ */
function preprocessKpis(kpis) {
  return kpis.map(kpi => {
    const rounds = kpi.rounds || [];

    // Find the latest round with any data (for subtitle / label display)
    const latestRound = (() => {
      if (rounds.length === 0) return null;
      for (let i = rounds.length - 1; i >= 0; i--) {
        const rid = rounds[i].id;
        if (kpi.rankings.some(r => r.round_values?.[rid] !== null && r.round_values?.[rid] !== undefined)) {
          return rounds[i];
        }
      }
      return rounds[rounds.length - 1];
    })();

    const isLatestMode = kpi.aggregation_mode === 'latest';

    // Attach displayValue (= total from backend, already mode-aware) and
    // currentValue (= this round's value only, for the subtitle)
    const enriched = kpi.rankings.map(r => ({
      ...r,
      displayValue: r.total,
      displayRank: r.rank,
      currentValue: latestRound ? (r.round_values?.[latestRound.id] ?? null) : null,
    }));

    const teamDisplayTotal = enriched.reduce((sum, r) => sum + (r.displayValue || 0), 0);

    return {
      ...kpi,
      rankings: enriched,
      latestRound,
      isLatestMode,
      teamDisplayTotal,
    };
  });
}

/* ═══════════════════════════════════════════════════════════════
   COMPUTE COMPOSITE SCORES — Goal-Based Weighted Scoring
   Modes: goal (actual/goal×100), relative_high (top=100),
          relative_low (lowest=100, for loss-style KPIs)
   Users with no data get null score and sort to bottom.
   ═══════════════════════════════════════════════════════════════ */
function computeCompositeRankings(kpis) {
  const userMap = {};
  for (const kpi of kpis) {
    for (const r of kpi.rankings) {
      if (!userMap[r.user_id]) {
        userMap[r.user_id] = { user_id: r.user_id, full_name: r.full_name, photo_url: r.photo_url, kpiScores: {} };
      }
    }
  }

  const compositeKpis = kpis.filter(k => k.include_in_composite !== false);
  const totalWeight = compositeKpis.reduce((sum, k) => sum + (k.weight || 100), 0);

  for (const kpi of kpis) {
    const mode = kpi.scoring_mode || 'goal';
    const includeInComposite = kpi.include_in_composite !== false;
    const weight = includeInComposite && totalWeight > 0 ? ((kpi.weight || 100) / totalWeight) : 0;
    const isNegative = kpi.value_sign === 'negative';

    const withData = kpi.rankings.filter(r => r.total !== null && r.total !== undefined);
    let topValue = null;
    let lowestValue = null;
    if (withData.length > 0) {
      const totals = withData.map(r => r.total);
      topValue = Math.max(...totals);
      lowestValue = Math.min(...totals);
    }

    for (const r of kpi.rankings) {
      let score = 0;
      if (r.total !== null && r.total !== undefined) {
        if (mode === 'goal' && kpi.goal && kpi.goal > 0) {
          score = (r.total / kpi.goal) * 100;
        } else if (mode === 'relative_high') {
          score = topValue > 0 ? (r.total / topValue) * 100 : 0;
        } else if (mode === 'relative_low') {
          if (r.total === 0) {
            score = 100;
          } else {
            score = lowestValue >= 0 && r.total > 0 ? (lowestValue / r.total) * 100 : 0;
          }
          if (topValue === 0) score = 100;
        } else if (mode === 'goal') {
          // goal mode but no goal set — fall back to relative_high
          score = topValue > 0 ? (r.total / topValue) * 100 : 0;
        }
      }

      if (userMap[r.user_id]) {
        userMap[r.user_id].kpiScores[kpi.id] = {
          score: Math.round(score * 10) / 10,
          total: r.total,
          rank: r.rank,
          weight,
          isNegative,
          excluded: !includeInComposite,
        };
      }
    }
  }

  const users = Object.values(userMap).map(u => {
    let compositeScore = 0;
    let hasAnyData = false;
    for (const kpi of compositeKpis) {
      const s = u.kpiScores[kpi.id];
      const w = s ? s.weight : 0;
      if (!s || s.total === null || s.total === undefined) {
        continue;
      }
      hasAnyData = true;
      if (s.isNegative) {
        const penalty = Math.max(0, 100 - s.score);
        compositeScore -= penalty * w;
      } else {
        compositeScore += s.score * w;
      }
    }
    return {
      ...u,
      compositeScore: hasAnyData ? Math.round(compositeScore * 10) / 10 : null,
    };
  });

  const sorted = [...users].sort((a, b) => {
    if (a.compositeScore === null && b.compositeScore === null) return 0;
    if (a.compositeScore === null) return 1;
    if (b.compositeScore === null) return -1;
    return b.compositeScore - a.compositeScore;
  });

  let rank = 0, lastScore = null;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].compositeScore === null) { sorted[i].rank = null; continue; }
    if (sorted[i].compositeScore !== lastScore) rank = i + 1;
    sorted[i].rank = rank;
    lastScore = sorted[i].compositeScore;
  }
  return sorted;
}

/* ═══════════════════════════════════════════════════════════════
   PAGE HEADER — title + period switcher + actions
   ═══════════════════════════════════════════════════════════════ */
function PageHeader({ title, onRefresh, refreshing, lastSynced }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 20, flexWrap: 'wrap', gap: 12,
    }}>
      <div>
        <h1 style={{
          margin: 0, fontSize: 26, fontWeight: 900, color: '#0f172a',
          letterSpacing: -0.5,
        }}>{title}</h1>
        {lastSynced && (
          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 4 }}>
            Last synced {lastSynced}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            padding: '9px 16px', borderRadius: 10, border: '1px solid #e2e8f0',
            background: '#fff', fontSize: 13, fontWeight: 700, color: '#0f172a',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s',
          }}
        >
          <span style={{
            display: 'inline-block',
            transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
            transition: 'transform 0.6s ease-out',
          }}>↻</span>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO CARD — personal stats with bold purple gradient
   ═══════════════════════════════════════════════════════════════ */
function HeroCard({ user, userEntry, totalUsers, kpiCount, context }) {
  if (!userEntry) return null;

  const rank = userEntry.rank;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 20,
      background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 40%, #7c3aed 100%)',
      padding: '28px 32px',
      marginBottom: 16,
      boxShadow: '0 10px 30px rgba(109, 40, 217, 0.25)',
    }}>
      {/* Decorative shapes */}
      <div style={{
        position: 'absolute', top: -60, right: -60, width: 240, height: 240,
        borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: -40, right: 80, width: 140, height: 140,
        borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {/* Avatar */}
        <div style={{
          width: 76, height: 76, borderRadius: '50%', flexShrink: 0,
          background: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 900, color: '#fff',
          border: '3px solid rgba(255,255,255,0.3)',
          backdropFilter: 'blur(10px)',
        }}>
          {userEntry.photo_url ? (
            <img src={userEntry.photo_url} alt={userEntry.full_name}
                 style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
          ) : ini(userEntry.full_name)}
        </div>

        {/* Main stats */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4 }}>
            Welcome back
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: -0.5, marginBottom: 10 }}>
            {userEntry.full_name}
          </div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Overall Rank
              </div>
              <div style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {rank ? `#${rank}` : '—'}
                <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginLeft: 6 }}>
                  of {totalUsers}
                </span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                KPIs Tracked
              </div>
              <div style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {kpiCount}
              </div>
            </div>
          </div>
          {context && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 12, fontWeight: 500 }}>
              {context}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PODIUM CARD — variable-size podium (one slot per ranked entry)
   ═══════════════════════════════════════════════════════════════ */

// Per-rank visual style. Top 3 use the traditional medal colors;
// ranks 4+ get a cool palette that fades toward violet.
function podiumSlotStyle(rank, totalSlots) {
  if (rank === 1) return { h: 150, av: 72, gradient: 'linear-gradient(180deg, #fde047, #eab308)', ring: '#eab308', medal: '🥇' };
  if (rank === 2) return { h: 115, av: 56, gradient: 'linear-gradient(180deg, #e5e7eb, #9ca3af)', ring: '#9ca3af', medal: '🥈' };
  if (rank === 3) return { h:  88, av: 50, gradient: 'linear-gradient(180deg, #fb923c, #c2410c)', ring: '#c2410c', medal: '🥉' };
  // 4+ — shrink pillar in 12px steps, floor at 38px, with a cool palette
  const h  = Math.max(38, 88 - (rank - 3) * 14);
  const av = Math.max(40, 50 - (rank - 3) * 3);
  const palette = [
    { gradient: 'linear-gradient(180deg, #93c5fd, #3b82f6)', ring: '#3b82f6' },
    { gradient: 'linear-gradient(180deg, #a5b4fc, #6366f1)', ring: '#6366f1' },
    { gradient: 'linear-gradient(180deg, #c4b5fd, #8b5cf6)', ring: '#8b5cf6' },
    { gradient: 'linear-gradient(180deg, #ddd6fe, #a78bfa)', ring: '#a78bfa' },
  ];
  const p = palette[(rank - 4) % palette.length];
  return { h, av, ...p, medal: null };
}

// Visual ordering: 1st in the middle, even ranks fan out to the left,
// odd ranks fan out to the right. Produces 6, 4, 2, 1, 3, 5 left-to-right.
function buildPodiumOrder(rankedEntries) {
  const ordered = [];
  for (const r of rankedEntries) {
    if (r.rank === 1) ordered.push(r);
    else if (r.rank % 2 === 0) ordered.unshift(r); // evens go left
    else ordered.push(r);                          // odds go right
  }
  return ordered;
}

function PodiumCard({ rankings, currentUserId }) {
  // Show one slot for EVERY ranked entry (not just top 3). Inactive
  // members are already filtered server-side, so anyone with a
  // non-null rank belongs on the podium.
  const ranked = [...rankings]
    .filter(r => r.rank !== null && r.rank !== undefined)
    .sort((a, b) => a.rank - b.rank);
  if (ranked.length === 0) return null;

  const ordered = buildPodiumOrder(ranked);
  const n = ranked.length;
  // Tighten gap + max-width as the podium grows so it still fits the card.
  const gap = n <= 3 ? 14 : Math.max(6, 14 - (n - 3) * 2);
  const maxWidth = n <= 3 ? 150 : Math.max(80, 150 - (n - 3) * 14);

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 16, marginBottom: 16,
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
      boxShadow: '0 4px 16px rgba(49, 46, 129, 0.3)',
      padding: '18px 24px 0',
    }}>
      {/* Decorative radial light */}
      <div style={{
        position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
        width: 320, height: 320, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(251, 191, 36, 0.15) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: 20, right: 20, width: 80, height: 80,
        borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none',
      }} />

      {/* Header inline */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, position: 'relative' }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
            Top Performers
          </div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: -0.3, marginTop: 1 }}>
            🏆 Podium
          </div>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
          This Period
        </div>
      </div>

      {/* Pillars */}
      <div style={{
        position: 'relative',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        gap,
      }}>
        {ordered.map((entry) => {
          const isYou = entry.user_id === currentUserId;
          const style = podiumSlotStyle(entry.rank, n);
          const labelFont = entry.rank === 1 ? 13 : entry.rank <= 3 ? 11 : 10;
          const rankFont  = entry.rank === 1 ? 28 : entry.rank <= 3 ? 22 : 18;
          return (
            <div key={entry.user_id ?? entry.rank} style={{ flex: 1, maxWidth, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {style.medal ? (
                <div style={{ fontSize: entry.rank === 1 ? 26 : 18, marginBottom: 2, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>{style.medal}</div>
              ) : (
                // Spacer keeps avatars aligned vertically when there's no medal
                <div style={{ height: 22, marginBottom: 2 }} />
              )}
              {entry.photo_url ? (
                <img src={entry.photo_url} alt={entry.full_name} style={{
                  width: style.av, height: style.av, borderRadius: '50%', objectFit: 'cover',
                  border: `3px solid ${style.ring}`,
                  boxShadow: `0 0 20px ${style.ring}66`,
                  marginBottom: 6,
                }} />
              ) : (
                <div style={{
                  width: style.av, height: style.av, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: entry.rank === 1 ? 20 : 15, fontWeight: 800, color: '#fff',
                  border: `3px solid ${style.ring}`,
                  boxShadow: `0 0 20px ${style.ring}66`,
                  marginBottom: 6,
                }}>{ini(entry.full_name)}</div>
              )}
              <div style={{
                fontSize: labelFont, fontWeight: 800, color: '#fff',
                textAlign: 'center', marginBottom: 2,
                maxWidth: maxWidth - 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}>
                {entry.full_name}
                {isYou && <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#3b82f6', padding: '1px 5px', borderRadius: 3, marginLeft: 4, verticalAlign: 'middle' }}>YOU</span>}
              </div>
              <div style={{ marginBottom: 8 }} />
              <div style={{
                width: '100%', height: style.h, borderRadius: '10px 10px 0 0',
                background: style.gradient, display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', padding: '8px 0',
                boxShadow: 'inset 0 -8px 16px rgba(0,0,0,0.2), 0 -2px 8px rgba(0,0,0,0.15)',
              }}>
                <span style={{
                  fontSize: rankFont, fontWeight: 900, color: '#fff',
                  textShadow: '0 2px 4px rgba(0,0,0,0.35)',
                }}>
                  {entry.rank}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CARD WRAPPER — reusable card shell
   ═══════════════════════════════════════════════════════════════ */
function Card({ title, subtitle, action, children, accent }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      padding: 20, marginBottom: 16,
      borderLeft: accent ? `4px solid ${accent}` : '1px solid #e2e8f0',
    }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <h3 style={{
                margin: 0, fontSize: 11, fontWeight: 800, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: 0.8,
              }}>{title}</h3>
            )}
            {subtitle && (
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                {subtitle}
              </div>
            )}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   KPI CARD — single KPI with rank bars
   ═══════════════════════════════════════════════════════════════ */
function KpiCardV2({ kpi, currentUserId, showWeight }) {
  const { rankings, metric_type, value_sign, weight, goal, cumulative_label, isLatestMode } = kpi;
  const isNegative = value_sign === 'negative';
  const isDisplayOnly = kpi.include_in_composite === false;

  const latestRound = kpi.latestRound;
  const teamDisplayTotal = kpi.teamDisplayTotal || 0;
  const hasMultipleRounds = (kpi.rounds || []).length > 1;
  // Show the secondary subtitle only when it adds information
  const showSecondary = hasMultipleRounds && latestRound && !isLatestMode;

  // Rankings are already sorted by displayValue with displayRank assigned
  const withValue = rankings.filter(r => r.displayValue !== null && r.displayValue !== undefined);
  const maxVal = withValue.length > 0 ? Math.max(...withValue.map(r => Math.abs(r.displayValue || 0)), 1) : 1;
  const leader = withValue[0];

  const accent = isNegative ? '#b91c1c' : '#6d28d9';
  const accentBg = isNegative ? '#fef2f2' : '#f5f3ff';
  const accentBorder = isNegative ? '#fecaca' : '#ddd6fe';

  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', padding: 18,
      display: 'flex', flexDirection: 'column', minHeight: 260,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Top-left accent stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: isNegative
          ? 'linear-gradient(90deg, #dc2626, #b91c1c)'
          : 'linear-gradient(90deg, #8b5cf6, #6d28d9)',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
            {isNegative && <span style={{ color: '#b91c1c' }}>➖ </span>}
            {kpi.name}
          </div>
          {latestRound && (
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>
              {latestRound.label}{latestRound.subtitle ? ` · ${latestRound.subtitle}` : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {isDisplayOnly && (
            <span style={{
              fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
              background: '#fef3c7', color: '#92400e', letterSpacing: 0.3,
            }}>INFO</span>
          )}
          {showWeight && weight && (
            <span style={{
              fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
              background: accentBg, color: accent, letterSpacing: 0.3,
            }}>{weight}%</span>
          )}
        </div>
      </div>

      {/* Hero value: primary display (backend total, mode-aware) */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <div style={{
            fontSize: 32, fontWeight: 900, color: accent, lineHeight: 1,
            fontVariantNumeric: 'tabular-nums', letterSpacing: -1,
          }}>
            {leader ? fmt(leader.displayValue, metric_type, value_sign) : '—'}
          </div>
          {/* Secondary subtitle: shows "this period" contribution for sum-mode
              KPIs with multiple rounds. Latest-mode KPIs already display the
              latest round value as the primary, so no subtitle needed. */}
          {showSecondary && leader && leader.currentValue !== null && leader.currentValue !== undefined && (
            <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>
              (<span style={{ color: accent }}>{fmt(leader.currentValue, metric_type, value_sign)}</span> this {latestRound?.label || 'period'})
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 3 }}>
          {leader ? <>Leader: <b style={{ color: '#0f172a' }}>{leader.full_name}</b> · {isLatestMode ? (latestRound?.label || 'Current') : (cumulative_label || 'Cumulative')}</> : 'No data'}
        </div>
      </div>

      {/* Rep display — per-round table for multi-round KPIs, bars otherwise */}
      {(kpi.rounds || []).length > 1 && !isLatestMode ? (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '6px 4px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>Rep</th>
                {(kpi.rounds || []).map(r => (
                  <th key={r.id} style={{ padding: '6px 4px', textAlign: 'right', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.2 }}>
                    <div>{r.label}</div>
                    {r.subtitle && <div style={{ fontSize: 8, color: '#cbd5e1', fontWeight: 500 }}>{r.subtitle}</div>}
                  </th>
                ))}
                <th style={{ padding: '6px 4px', textAlign: 'right', fontSize: 10, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: 0.3, borderLeft: `2px solid ${accentBorder}` }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {withValue.length === 0 ? (
                <tr><td colSpan={(kpi.rounds || []).length + 2} style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>No data</td></tr>
              ) : withValue.map((r, idx) => {
                const isYou = r.user_id === currentUserId;
                const isTop = idx === 0;
                return (
                  <tr key={r.user_id} style={{
                    borderBottom: idx < withValue.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: isYou ? '#eff6ff' : 'transparent',
                  }}>
                    <td style={{ padding: '8px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 900,
                        background: isTop ? accent : '#f1f5f9',
                        color: isTop ? '#fff' : '#64748b',
                      }}>{idx + 1}</div>
                      <span style={{
                        fontSize: 12, fontWeight: isYou ? 800 : 600,
                        color: isYou ? '#1e40af' : '#0f172a',
                      }}>{r.full_name.split(' ')[0]}</span>
                      {isYou && <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#3b82f6', padding: '1px 4px', borderRadius: 3 }}>YOU</span>}
                    </td>
                    {(kpi.rounds || []).map(round => {
                      const v = r.round_values?.[round.id];
                      return (
                        <td key={round.id} style={{
                          padding: '8px 4px', textAlign: 'right',
                          fontSize: 12, fontWeight: 600,
                          color: v !== null && v !== undefined ? '#475569' : '#cbd5e1',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {v !== null && v !== undefined ? fmt(v, metric_type, value_sign) : '—'}
                        </td>
                      );
                    })}
                    <td style={{
                      padding: '8px 4px', textAlign: 'right',
                      fontSize: 13, fontWeight: 900,
                      color: isNegative ? '#b91c1c' : accent,
                      fontVariantNumeric: 'tabular-nums',
                      borderLeft: `2px solid ${accentBorder}`,
                      background: isYou ? '#dbeafe' : accentBg,
                    }}>
                      {fmt(r.displayValue, metric_type, value_sign)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {withValue.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No data</div>
          ) : withValue.map((r, idx) => {
            const isYou = r.user_id === currentUserId;
            const barPct = (Math.abs(r.displayValue || 0) / maxVal) * 100;
            const isTop = idx === 0;
            return (
              <div key={r.user_id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', borderRadius: 6,
                background: isYou ? '#eff6ff' : 'transparent',
                border: isYou ? '1px solid #bfdbfe' : '1px solid transparent',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 900,
                  background: isTop ? accent : '#f1f5f9',
                  color: isTop ? '#fff' : '#64748b',
                }}>{idx + 1}</div>
                <div style={{
                  flex: 1, fontSize: 12, fontWeight: isYou ? 800 : 600,
                  color: isYou ? '#1e40af' : '#0f172a',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {r.full_name.split(' ')[0]}
                  {isYou && <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#3b82f6', padding: '1px 4px', borderRadius: 3, marginLeft: 4 }}>YOU</span>}
                </div>
                <div style={{ flex: 1.5, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${barPct}%`,
                    background: isTop
                      ? (isNegative ? 'linear-gradient(90deg, #ef4444, #b91c1c)' : 'linear-gradient(90deg, #8b5cf6, #6d28d9)')
                      : (isNegative ? '#fca5a5' : '#c4b5fd'),
                    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} />
                </div>
                <div style={{
                  width: 52, textAlign: 'right', fontSize: 12, fontWeight: 800,
                  color: isNegative ? '#b91c1c' : '#0f172a',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {fmt(r.displayValue, metric_type, value_sign)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer: goal progress (uses display total matching mode) */}
      {goal && !isNegative && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 4 }}>
            <span style={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Team Goal</span>
            <span style={{ color: '#0f172a', fontWeight: 800 }}>
              {fmt(teamDisplayTotal, metric_type)} / {fmt(goal, metric_type)}
            </span>
          </div>
          <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.min(100, (teamDisplayTotal / goal) * 100)}%`,
              background: 'linear-gradient(90deg, #8b5cf6, #6d28d9)',
              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   KPI CARDS RESPONSIVE — grid on desktop, carousel on mobile
   ═══════════════════════════════════════════════════════════════ */
function KpiCardsResponsive({ kpis, currentUserId, showWeight }) {
  const scrollRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  function handleScroll() {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const cardWidth = el.offsetWidth;
    const idx = Math.round(el.scrollLeft / cardWidth);
    if (idx !== activeIdx) setActiveIdx(idx);
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a', letterSpacing: -0.3 }}>
          Individual KPIs
        </h2>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>
          {kpis.length} tracked
        </div>
      </div>

      {/* Responsive container — grid on desktop, horizontal scroll on mobile */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="lb-kpi-grid"
      >
        {kpis.map(kpi => (
          <div key={kpi.id} className="lb-kpi-slot">
            <KpiCardV2 kpi={kpi} currentUserId={currentUserId} showWeight={showWeight} />
          </div>
        ))}
      </div>

      {/* Carousel dots (mobile only, shown via CSS) */}
      <div className="lb-carousel-dots">
        {kpis.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === activeIdx ? 20 : 6, height: 6, borderRadius: 3,
              background: i === activeIdx ? '#6d28d9' : '#cbd5e1',
              transition: 'all 0.3s',
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FULL TEAM RANKINGS TABLE
   ═══════════════════════════════════════════════════════════════ */
function TeamRankingsTable({ rankings, kpis, currentUserId, canSeeWeights }) {
  return (
    <Card title="Full Team" subtitle="All reps ranked by overall performance">
      <div style={{ overflowX: 'auto', margin: '-6px -4px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 800, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 180 }}>
                # Rep
              </th>
              {kpis.map(k => (
                <th key={k.id} style={{ padding: '10px 10px', textAlign: 'center', fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
                  {k.name}
                  {canSeeWeights && k.weight && (
                    <div style={{ fontSize: 9, color: '#cbd5e1', fontWeight: 600, marginTop: 1 }}>{k.weight}%</div>
                  )}
                </th>
              ))}
              <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 800, color: '#6d28d9', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, background: 'linear-gradient(180deg, #faf5ff, #f3e8ff)', borderLeft: '2px solid #ede9fe' }}>
                Overall
              </th>
            </tr>
          </thead>
          <tbody>
            {rankings.map((entry, idx) => {
              const isYou = entry.user_id === currentUserId;
              const rank = entry.rank;
              const rc = rank && rank <= 3 ? medalColors[rank - 1] : '#94a3b8';
              return (
                <tr key={entry.user_id} style={{
                  borderBottom: idx < rankings.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: isYou ? '#eff6ff' : 'transparent',
                }}>
                  <td style={{ padding: '12px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 900, color: rank <= 3 ? '#fff' : rc,
                      background: rank && rank <= 3 ? rc : '#f8fafc',
                    }}>{rank || '\u2014'}</span>
                    {entry.photo_url ? (
                      <img src={entry.photo_url} alt={entry.full_name} style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', border: '1.5px solid #e2e8f0' }} />
                    ) : (
                      <span style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9' }}>{ini(entry.full_name)}</span>
                    )}
                    <span style={{ fontWeight: isYou ? 800 : 700, color: isYou ? '#1e40af' : '#0f172a' }}>{entry.full_name}</span>
                    {isYou && <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#3b82f6', padding: '2px 5px', borderRadius: 3 }}>YOU</span>}
                  </td>
                  {kpis.map(k => {
                    const kpiScore = entry.kpiScores[k.id];
                    const kpiRank = kpiScore?.rank;
                    const kpiUser = k.rankings.find(r => r.user_id === entry.user_id);
                    const displayValue = kpiUser?.displayValue;
                    return (
                      <td key={k.id} style={{ padding: '12px 10px', textAlign: 'center' }}>
                        {kpiRank ? (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 800, color: k.value_sign === 'negative' ? '#b91c1c' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(displayValue, k.metric_type, k.value_sign)}
                            </div>
                            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', marginTop: 1 }}>
                              #{kpiRank}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: '12px 16px', textAlign: 'center', fontWeight: 900, fontSize: 16, color: '#6d28d9',
                    fontVariantNumeric: 'tabular-nums',
                    background: isYou ? '#e0d4ff' : 'linear-gradient(180deg, #faf5ff, #f3e8ff)',
                    borderLeft: '2px solid #ede9fe',
                  }}>
                    {entry.rank !== null ? `#${entry.rank}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SINGLE KPI VIEW — simpler layout when only one KPI in group
   ═══════════════════════════════════════════════════════════════ */
function SingleKpiView({ kpi, user, canSeeWeights }) {
  const rankings = [...(kpi.rankings || [])].sort((a, b) => {
    if (a.rank === null) return 1;
    if (b.rank === null) return -1;
    return a.rank - b.rank;
  });

  return (
    <>
      <PodiumCard
        rankings={rankings.map(r => ({ ...r, compositeScore: r.displayValue }))}
        currentUserId={user?.id}
      />
      <KpiCardV2 kpi={kpi} currentUserId={user?.id} showWeight={canSeeWeights} />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function LeaderboardPage() {
  const { groupName } = useParams();
  const { user, can } = useAuth();
  const canSeeWeights = can ? can('manage_admin') : false;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const result = await api.getLeaderboard(groupName);
      setData(result);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load leaderboard');
    }
    setLoading(false);
    setRefreshing(false);
  }, [groupName]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
          <div className="loading-spinner" />
          <div style={{ marginTop: 12, fontSize: 13 }}>Loading leaderboard…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!data || !data.kpis || data.kpis.length === 0) {
    return (
      <div className="admin-page">
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>{GROUP_LABELS[groupName] || data?.group_label || groupName} Leaderboard</h1>
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', marginTop: 20 }}>
          <p style={{ fontSize: 17, fontWeight: 700, color: '#475569' }}>No KPIs configured for this group yet.</p>
        </div>
      </div>
    );
  }

  const isSingleKpi = data.kpis.length === 1;
  const title = `${GROUP_LABELS[groupName] || data.group_label || groupName} Leaderboard`;

  // Pre-process KPIs: attach currentValue (from latest round) and re-rank
  // by that so the leaderboard matches the Data Entry page exactly.
  const processedKpis = preprocessKpis(data.kpis);

  // Multi-KPI: compute composite across all KPIs
  const compositeRankings = !isSingleKpi ? computeCompositeRankings(processedKpis) : [];
  const userEntry = compositeRankings.find(e => e.user_id === user?.id);
  const totalUsers = compositeRankings.length;

  // Build contextual message
  let context = '';
  if (userEntry && userEntry.rank !== null) {
    const ahead = compositeRankings.find(e => e.rank === userEntry.rank - 1);
    const behind = compositeRankings.find(e => e.rank === userEntry.rank + 1);
    const parts = [];
    if (ahead) {
      parts.push(`${ahead.full_name.split(' ')[0]} is one spot ahead`);
    }
    if (behind) {
      parts.push(`one spot ahead of ${behind.full_name.split(' ')[0]}`);
    }
    context = parts.join(' · ');
  }

  return (
    <div className="admin-page leaderboard-v2" style={{ background: '#f8fafc', minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <PageHeader
          title={title}
          onRefresh={() => load(true)}
          refreshing={refreshing}
        />

        {isSingleKpi ? (
          <SingleKpiView kpi={processedKpis[0]} user={user} canSeeWeights={canSeeWeights} />
        ) : (
          <>
            {userEntry && (
              <HeroCard
                user={user}
                userEntry={userEntry}
                totalUsers={totalUsers}
                kpiCount={processedKpis.length}
                context={context}
              />
            )}

            <PodiumCard rankings={compositeRankings} currentUserId={user?.id} />

            <KpiCardsResponsive
              kpis={processedKpis}
              currentUserId={user?.id}
              showWeight={canSeeWeights}
            />

            <TeamRankingsTable
              rankings={compositeRankings}
              kpis={processedKpis}
              currentUserId={user?.id}
              canSeeWeights={canSeeWeights}
            />
          </>
        )}
      </div>
    </div>
  );
}
